import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';

import { FlexBox } from 'ml-rubiks-styleguide/src/box';
import { Typography } from 'ml-rubiks-styleguide/src/typography';

import { isProcessingPaymentStatus } from '../../constants/payment-methods';
import { usePaymentErrorContext } from '../../contexts/payment-error-context';
import { usePaymentState } from '../../contexts/payment-state-context';
import { PaymentError } from '../../core/payment-error';
import { PaymentProcessor } from '../../core/payment-processor';
import { BankAccountDetailsDto, PaymentMethod } from '../../types';
import { AvailablePaymentMethodDto, CreditorDto } from '../../types/api';
import { LoadingMessage } from '../common';

const DirectDebitForm = lazy(() => import('../../providers/direct-debit'));

interface FormBasedPaymentProps {
    methodType: PaymentMethod;
    paymentProcessor: PaymentProcessor;
    availablePaymentMethod?: AvailablePaymentMethodDto;
    creditor?: CreditorDto;
    onPaymentSuccess?: () => void;
    hidePaymentButton?: boolean;
}

export const FormBasedPayment: React.FC<FormBasedPaymentProps> = ({
    methodType,
    paymentProcessor,
    availablePaymentMethod,
    creditor,
    onPaymentSuccess,
    hidePaymentButton
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const formRef = useRef<{ submit: () => Promise<void> }>(null);
    const { registerSubmitHandler, setProcessing, setCanSubmit } =
        usePaymentState();
    const { addError } = usePaymentErrorContext();

    useEffect(() => {
        setProcessing(isLoading);
    }, [isLoading, setProcessing]);

    useEffect(() => {
        if (hidePaymentButton) {
            registerSubmitHandler(async () => {
                if (!formRef.current) {
                    throw new PaymentError(
                        'Form not ready',
                        'VALIDATION_ERROR'
                    );
                }
                await formRef.current.submit();
            });
        }
    }, [hidePaymentButton, registerSubmitHandler]);

    if (
        methodType === 'SEPA' ||
        methodType === 'CH_DD' ||
        methodType === 'LSV'
    ) {
        const requireDirectDebitSignature =
            availablePaymentMethod?.requireDirectDebitSignature;

        const handleDirectDebitSubmit = async (
            bankDetails: BankAccountDetailsDto
        ) => {
            setIsLoading(true);

            try {
                const existingPayment = paymentProcessor.hasPaymentRequestToken()
                    ? await paymentProcessor.getPayment()
                    : null;

                if (
                    !existingPayment?.status ||
                    !isProcessingPaymentStatus(existingPayment.status)
                ) {
                    await paymentProcessor.initiatePayment({
                        paymentMethod: methodType
                    });
                }

                await paymentProcessor.processPayment(
                    { paymentMethodDetails: JSON.stringify(bankDetails) },
                    undefined,
                    {
                        onSettled: () => setIsLoading(false),
                        onSucceeded: onPaymentSuccess,
                        onError: addError
                    }
                );
            } catch (error) {
                setIsLoading(false);
                addError(
                    PaymentError.fromUnknown(
                        error,
                        'PSP_ERROR',
                        'Failed to process SEPA payment'
                    )
                );
            }
        };

        return (
            <Suspense fallback={<LoadingMessage message="Loading" />}>
                <DirectDebitForm
                    ref={formRef}
                    paymentMethodType={methodType}
                    onSubmit={handleDirectDebitSubmit}
                    creditor={creditor}
                    requireDirectDebitSignature={requireDirectDebitSignature}
                    isLoading={isLoading}
                    hidePaymentButton={hidePaymentButton}
                    onValidationChange={
                        hidePaymentButton ? setCanSubmit : undefined
                    }
                    paymentProcessor={paymentProcessor}
                />
            </Suspense>
        );
    }

    return (
        <FlexBox sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary">
                Form-based payment method &apos;{methodType}&apos; not yet
                implemented
            </Typography>
        </FlexBox>
    );
};
